import { showNotice } from '../ui/appNotice'

// A short history of corpus changes (trash, moves, renames) that can be taken
// back from the Undo button on their notice or with Ctrl+Z in the corpus panel
// or folder overview. Text edits keep their own undo inside the editor.

interface CorpusUndoEntry {
  /** What was done, e.g. “Moved “Notes” into “Drafts”” */
  message: string
  undo: () => unknown
}

const HISTORY_LIMIT = 30
const history: CorpusUndoEntry[] = []

/**
 * Remembers how to reverse a change. `announce` also shows the change in a
 * notice with an Undo button, for changes the writer might not have meant.
 */
export function recordCorpusUndo(message: string, undo: () => unknown, options: { announce?: boolean } = {}) {
  const entry: CorpusUndoEntry = { message, undo }
  history.push(entry)
  if (history.length > HISTORY_LIMIT) history.shift()
  if (options.announce) {
    showNotice({ message, action: { label: 'Undo', run: () => { void runUndo(entry) } } })
  }
}

/** Reverses the most recent change. Returns false when there is nothing to undo. */
export function undoLastCorpusChange(): boolean {
  const entry = history[history.length - 1]
  if (!entry) return false
  void runUndo(entry)
  return true
}

async function runUndo(entry: CorpusUndoEntry) {
  const index = history.indexOf(entry)
  if (index === -1) return
  history.splice(index, 1)
  try {
    await entry.undo()
    showNotice({ message: `Undone: ${entry.message}` })
  } catch (error) {
    showNotice({ message: `Couldn’t undo: ${error instanceof Error ? error.message : String(error)}`, tone: 'error' })
  }
}
